import React from 'react';
import { Package, ChevronRight, Sparkles, Trophy } from 'lucide-react';
import { LootboxData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardLootboxStatsProps {
  loading: boolean;
  data: LootboxData | null;
}
export const CardLootboxStats: React.FC<CardLootboxStatsProps> = ({
  loading,
  data
}) => {
  if (loading) return <CardSkeleton data-id="element-20830" />;
  if (!data) return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20831">
        <div className="flex items-center justify-between mb-4" data-id="element-20832">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-purple-300 dark:to-indigo-200" data-id="element-20833">
            <Package className="text-purple-500 dark:text-purple-400" size={18} data-id="element-20834" />
            Lootbox Stats
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20835">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-20836">
            Complete rituals to earn lootboxes containing rewards.
          </p>
        </div>
      </div>;
  // Calculate open rate percentage
  const openRate = data.total > 0 ? Math.round(data.opened / data.total * 100) : 0;
  // Helper function to get rarity color
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'from-gray-400/80 to-gray-500/80';
      case 'rare':
        return 'from-blue-400/80 to-blue-500/80';
      case 'epic':
        return 'from-purple-400/80 to-purple-500/80';
      case 'legendary':
        return 'from-amber-400/80 to-amber-500/80';
      default:
        return 'from-gray-400/80 to-gray-500/80';
    }
  };
  return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20837">
      <div className="relative" data-id="element-20838">
        <div className="flex items-center justify-between mb-4" data-id="element-20839">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-purple-300 dark:to-indigo-200" data-id="element-20840">
            <Package className="text-purple-500 dark:text-purple-400" size={18} data-id="element-20841" />
            Lootbox Stats
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-20842">
          <div className="bg-purple-50 dark:bg-purple-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-purple-200/50 dark:border-purple-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20843">
            <div className="text-purple-500 dark:text-purple-300 text-xs mb-1" data-id="element-20844">
              Total Lootboxes
            </div>
            <p className="font-bold text-xl text-purple-600 dark:text-purple-200 etched-text" data-id="element-20845">
              {data.total}
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-blue-200/50 dark:border-blue-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.1)]" data-id="element-20846">
            <div className="text-blue-500 dark:text-blue-300 text-xs mb-1" data-id="element-20847">
              Open Rate
            </div>
            <p className="font-bold text-xl text-blue-600 dark:text-blue-200 etched-text" data-id="element-20848">
              {openRate}%
            </p>
            <p className="text-xs text-blue-500 dark:text-blue-300" data-id="element-20849">
              {data.opened} opened, {data.missedCount} missed
            </p>
          </div>
        </div>
        {/* Best Pull */}
        <div className="mb-4" data-id="element-20850">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20851">
            Best Pull
          </h4>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3 border border-purple-200/50 dark:border-purple-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20852">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${getRarityColor(data.bestPull.rarity)} border border-${data.bestPull.rarity === 'legendary' ? 'amber' : data.bestPull.rarity === 'epic' ? 'purple' : data.bestPull.rarity === 'rare' ? 'blue' : 'gray'}-300/50 dark:border-${data.bestPull.rarity === 'legendary' ? 'amber' : data.bestPull.rarity === 'epic' ? 'purple' : data.bestPull.rarity === 'rare' ? 'blue' : 'gray'}-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.3)] vessel-glow`} data-id="element-20853">
              <Sparkles size={20} className="text-white" data-id="element-20854" />
            </div>
            <div data-id="element-20855">
              <div className="flex items-center gap-2" data-id="element-20856">
                <h4 className="font-medium text-sm text-gray-700 dark:text-indigo-200" data-id="element-20857">
                  {data.bestPull.name}
                </h4>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm bg-gradient-to-r ${getRarityColor(data.bestPull.rarity)} text-white`} data-id="element-20858">
                  {data.bestPull.rarity.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Reward breakdown */}
        <div className="mb-4" data-id="element-20859">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20860">
            Reward Distribution
          </h4>
          <div className="grid grid-cols-4 gap-2" data-id="element-20861">
            <div className="bg-pink-50 dark:bg-pink-900/30 backdrop-blur-sm rounded-lg p-2 text-center border border-pink-200/50 dark:border-pink-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(236,72,153,0.1)]" data-id="element-20862">
              <p className="text-pink-500 dark:text-pink-300 text-xs" data-id="element-20863">Tokens</p>
              <p className="font-medium text-pink-600 dark:text-pink-200 etched-text" data-id="element-20864">
                {data.breakdown.tokens}
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/30 backdrop-blur-sm rounded-lg p-2 text-center border border-blue-200/50 dark:border-blue-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.1)]" data-id="element-20865">
              <p className="text-blue-500 dark:text-blue-300 text-xs" data-id="element-20866">Gems</p>
              <p className="font-medium text-blue-600 dark:text-blue-200 etched-text" data-id="element-20867">
                {data.breakdown.gems}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 backdrop-blur-sm rounded-lg p-2 text-center border border-purple-200/50 dark:border-purple-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20868">
              <p className="text-purple-500 dark:text-purple-300 text-xs" data-id="element-20869">
                Skins
              </p>
              <p className="font-medium text-purple-600 dark:text-purple-200 etched-text" data-id="element-20870">
                {data.breakdown.skins}
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/30 backdrop-blur-sm rounded-lg p-2 text-center border border-amber-200/50 dark:border-amber-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(251,191,36,0.1)]" data-id="element-20871">
              <p className="text-amber-500 dark:text-amber-300 text-xs" data-id="element-20872">
                VIP Days
              </p>
              <p className="font-medium text-amber-600 dark:text-amber-200 etched-text" data-id="element-20873">
                {data.breakdown.vipDays}
              </p>
            </div>
          </div>
        </div>
        {/* Next unlock */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 backdrop-blur-sm rounded-lg p-4 mb-4 border border-green-200/50 dark:border-green-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(16,185,129,0.1)]" data-id="element-20874">
          <div className="flex items-center gap-2 mb-2" data-id="element-20875">
            <Trophy size={16} className="text-green-500 dark:text-green-400" data-id="element-20876" />
            <h4 className="font-medium text-sm text-green-600 dark:text-green-200" data-id="element-20877">
              Next unlock: {data.nextUnlock.reward}
            </h4>
          </div>
          <p className="text-sm text-green-500 dark:text-green-300" data-id="element-20878">
            {data.nextUnlock.ritualsLeft} ritual
            {data.nextUnlock.ritualsLeft !== 1 ? 's' : ''} remaining to unlock
          </p>
        </div>
        <div className="mt-4 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20" data-id="element-20879">
          <button className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 dark:from-purple-600 dark:to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center justify-center gap-1 border border-purple-300/30 dark:border-purple-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.2)]" onClick={() => {
          // Navigate to reward hub
          const event = new CustomEvent('navigate', {
            detail: {
              section: 'rewards'
            }
          });
          document.dispatchEvent(event);
        }} data-id="element-20880">
            <span data-id="element-20881">Open Lootboxes</span>
            <ChevronRight size={16} data-id="element-20882" />
          </button>
        </div>
      </div>
    </div>;
};